import { Injectable } from '@angular/core';
import { FrontendLanguage } from '../utils/languageUtils';
import { concat, filter, fromEventPattern, interval, Observable, take } from 'rxjs';
import { LanguageService } from './language/language.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { map } from 'rxjs/operators';

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class GenesysService {
  private readonly genesysName: string = 'Genesys';
  private readonly genesysScriptUrl: string = 'https://apps.mypurecloud.de/genesys-bootstrap/genesys.min.js';
  private readonly genesysEnvironment: string = 'prod-euc1';
  private deploymentIdMap = new Map<FrontendLanguage, string>();
  private temporaryGenesysQueue: never[] = [];

  private stubbedQueuingGenesysCommand = (...args: never[]): void => {
    // @ts-ignore
    this.temporaryGenesysQueue.push(args);
  };

  private windowWithGenesysCommand = window as unknown as Window & {
    // we are dependent on the 3rd party vendor for that name
    // eslint-disable-next-line @typescript-eslint/naming-convention
    Genesys: (arg1: string, arg2: string, arg3?: object, onSuccess?: () => void, onFailure?: () => void) => void;
  };

  constructor(private languageService: LanguageService) {
    this.deploymentIdMap.set('de', '64ddd9ae-534f-4734-83ea-93822913d574');
    this.deploymentIdMap.set('fr', '9f59c8dd-d5bc-4603-9381-c921942b5412');
    this.deploymentIdMap.set('it', '35c4efcd-9b21-40e2-bac3-081fe59ef37f');
  }

  public loadGenesysScript(): void {
    // @ts-ignore
    window['_genesysJs'] = this.genesysName;
    // @ts-ignore
    this.windowWithGenesysCommand.Genesys = this.stubbedQueuingGenesysCommand;
    this.languageService
      .getCurrentLanguage$()
      .pipe(untilDestroyed(this))
      .subscribe((lang) => {
        // @ts-ignore
        this.windowWithGenesysCommand.Genesys.c = {
          environment: this.genesysEnvironment,
          deploymentId: this.deploymentIdMap.get(lang as FrontendLanguage),
        };
      });

    let genesysScriptElement: HTMLScriptElement = document.createElement('script');
    genesysScriptElement.async = true;
    genesysScriptElement.src = this.genesysScriptUrl;
    document.head.appendChild(genesysScriptElement);
  }

  public chatAvailableInLanguage$(): Observable<boolean> {
    return this.languageService.getCurrentLanguage$().pipe(
      untilDestroyed(this),
      map((lang) => {
        return [...this.deploymentIdMap.keys()].includes(lang as FrontendLanguage);
      }),
    );
  }

  public chatReady$(): Observable<boolean> {
    return concat(this.pollGenesysVariable$(), this.subscribeToChatReadiness$());
  }

  private pollGenesysVariable$(): Observable<boolean> {
    return interval(50).pipe(
      filter(
        () =>
          // @ts-ignore
          window[this.genesysName] !== this.stubbedQueuingGenesysCommand,
      ),
      map(() => true),
      take(1),
    );
  }

  private subscribeToChatReadiness$(): Observable<boolean> {
    return fromEventPattern<boolean>((handler) =>
      this.windowWithGenesysCommand.Genesys('subscribe', 'Messenger.ready', () => {
        this.temporaryGenesysQueue.forEach((args) => {
          // @ts-ignore
          this.windowWithGenesysCommand.Genesys(...args);
        });
        this.temporaryGenesysQueue.length = 0;
        handler(true);
      }),
    ).pipe(take(1));
  }

  public openChat(): void {
    this.windowWithGenesysCommand.Genesys(
      'command',
      'Messenger.open',
      {},
      () => {},
      () => {
        this.windowWithGenesysCommand.Genesys('command', 'Messenger.close');
      },
    );
  }
}
